import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { AUTH_CLEARED_EVENT } from '../services/api';

const AUTH_BOOTSTRAP_MAX_MS = 20000;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('assetlens_token');

    // Never leave the app on a spinner if auth bootstrap stalls (e.g. hung refresh).
    const watchdog = setTimeout(() => {
      if (!cancelled) {
        localStorage.removeItem('assetlens_token');
        localStorage.removeItem('assetlens_refresh_token');
        setUser(null);
        setLoading(false);
      }
    }, AUTH_BOOTSTRAP_MAX_MS);

    if (token) {
      api.get('/api/auth/me')
        .then(res => { if (!cancelled) setUser(res.data); })
        .catch(() => {
          if (cancelled) return;
          localStorage.removeItem('assetlens_token');
          localStorage.removeItem('assetlens_refresh_token');
          setUser(null);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
          clearTimeout(watchdog);
        });
    } else {
      clearTimeout(watchdog);
      setLoading(false);
    }

    return () => {
      cancelled = true;
      clearTimeout(watchdog);
    };
  }, []);

  // api.js clears tokens on 401 but lives outside React — sync user state here.
  useEffect(() => {
    const onAuthCleared = () => setUser(null);
    window.addEventListener(AUTH_CLEARED_EVENT, onAuthCleared);
    return () => window.removeEventListener(AUTH_CLEARED_EVENT, onAuthCleared);
  }, []);

  const login = useCallback(async (email, password, recaptchaToken) => {
    const res = await api.post('/api/auth/login', {
      email,
      password,
      recaptcha_token: recaptchaToken || undefined,
    });
    localStorage.setItem('assetlens_token', res.data.access_token);
    localStorage.setItem('assetlens_refresh_token', res.data.refresh_token);
    localStorage.setItem('assetlens_user_email', res.data.user.email);
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const register = useCallback(async (data) => {
    const res = await api.post('/api/auth/register', data);
    return res.data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('assetlens_token');
    localStorage.removeItem('assetlens_refresh_token');
    localStorage.removeItem('assetlens_user_email');
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res = await api.get('/api/auth/me');
      setUser(res.data);
      return res.data;
    } catch {
      return null;
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
